// React imports
import React, { useContext, useEffect, useMemo, useState } from "react";

// Context imports
import { AccessTokenContext } from "../../../contexts/AccessTokenContext";

// UI elements imports
import DisplayBox from "../../../ui-elements/DisplayBox/DisplayBox";
import FileUploadDropzone from "../../../ui-elements/FileUploadDropZone/FileUploadDropZone";

// Modal imports
import RiskScoreModal from "../RiskScoreModal/RiskScoreModal";

// Component imports
import CompanyAnswersList from "./Components/CompanyAnswers";
import CompanyTitle from "../../Company/CompanyTitle/CompanyTitle";
import GildandiSkraningAdilar from "./Components/GildandiSkraningAdilar";
import GildandiSkraningGrunnupplysingar from "./Components/GildandiSkraningGrunnupplysingar";
import QuestionaireResults from "./Components/QuestionaireResults";
import AlthjodlegirValistar from "./Components/AlthjodlegirValistar";
import KYCNavBar from "../../Company/KYCNavBar/KYCNavBar";
import InfoHeader from "../../../ui-elements/InfoHeader/InfoHeader";
import CompanyTitleLoadingSkeleton from "../../Company/CompanyTitle/CompanyTitleLoadingSkeleton";
import PepWatchPEPConnectionModal from "../../PEP/PepWatchModals/PepWatchPEPConnectionModal";
import GoBackLink from "../../../ui-elements/GoBackLink/GoBackLink";
import EditContactModal from "../EditContactModal/EditContactModal";
import ForeignPepModal from "../../PEP/ForeignPepModal";
import { PepReviewUpdate } from "../../PEP/PepReviewControls";

// Type imports
import Alert, { AlertType } from "../../../ui-elements/Alert/Alert";
import { AnswerListOverviewDto, ForeignPepReport } from "../../../types/CompanyAnswersTypes";
import { ContactsOverview, IAdilarTableData, RiskScoreDto, RiskScoreFormValues } from "../../../types/DueDiligenceTypes";

// 3rd party imports
import { Toast, ToastContainer } from "react-bootstrap";
import Bugsnag from "@bugsnag/js";

// Config imports
import { TOAST_DELAY } from "../../../services/config";

import { CountryList } from "../../../assets/staticData/CountryList";
import { IPepArray } from "../../PEP/PepPage";
import { getCountryName } from "../../../services/utils";
import Logs from "./Components/Logs";
import { availableVersions, QuestionListType } from "./Constants";
import { bucketMatches } from "./shared/foreignPepBuckets";
import KYCSurveyProgressBar from "../../../ui-elements/KYCSurveyProgressBar/KYCSurveyProgressBar";
import GeniusLabel from "../../../ui-elements/GeniusLabel/GeniusLabel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/pro-solid-svg-icons";
import EditCustomerStatusModal from "../EditCustomerStatusModal/EditCustomerStatusModal";

const ViewAnswersPage: React.FC<{ customerId: string; riskscoreId: string; riskscore: RiskScoreDto, answerListId: number, active: boolean, activeDescription: string | null }> = ({
    customerId,
    riskscoreId,
    riskscore,
    answerListId,
    active: initialActive,
    activeDescription: initialActiveDescription
}) => {
    const accessToken = useContext(AccessTokenContext);

    const [stageData, setStageData] = useState<RiskScoreDto | Error | null | undefined>(undefined);
    const [data, setData] = useState<AnswerListOverviewDto | undefined | null | Error>(undefined);
    const [riskScore, setRiskScore] = useState<RiskScoreDto>(riskscore);
    const [riskScoreModal_isOpen, setRiskScoreModal_isOpen] = useState(false);
    const [riskScoreModal_data, setRiskScoreModal_data] = useState<RiskScoreFormValues | undefined>(undefined);
    const [answerListIndex, setAnswerListIndex] = useState<number>(0);

    const [editContactModal_isOpen, setEditContactModal_isOpen] = useState(false);
    const [editContactModal_data, setEditContactModal_data] = useState<{ customerId: number; companyName: string; confirmProcurations: boolean }>({ customerId: 0, companyName: '', confirmProcurations: false });
    const [editContactModal_contacts, setEditContactModal_contacts] = useState<ContactsOverview | null>(null);

    const [editCustomerStatusModal_isOpen, setEditCustomerStatusModal_isOpen] = useState(false);
    const [isActive, setIsActive] = useState(initialActive);
    const [currentActiveDescription, setCurrentActiveDescription] = useState(initialActiveDescription);

    // Foreign PEP Modal state - holds the whole report so we can read/write its false-positive flag & comment
    const [foreignPepModal_isOpen, setForeignPepModal_isOpen] = useState(false);
    const [foreignPepModal_report, setForeignPepModal_report] = useState<ForeignPepReport | undefined>(undefined);

    // Keep both the open modal and the underlying data in sync after a successful flag/comment write
    const handleForeignPepReviewChange = (reportId: number, update: PepReviewUpdate) => {
        setData(prev => {
            if (!prev || prev instanceof Error) return prev;
            return {
                ...prev,
                foreignPepReports: prev.foreignPepReports?.map(r => r.id === reportId ? { ...r, ...update } : r)
            };
        });
        setForeignPepModal_report(prev => prev && prev.id === reportId ? { ...prev, ...update } : prev);
    };

    // Reputational-risk (Orðsporsáhætta) modal — same reports as the válistar modal, but an independent verdict
    const [foreignRepModal_isOpen, setForeignRepModal_isOpen] = useState(false);
    const [foreignRepModal_report, setForeignRepModal_report] = useState<ForeignPepReport | undefined>(undefined);

    const handleForeignRepReviewChange = (reportId: number, update: PepReviewUpdate) => {
        // PepReviewControls reports generic { falsePositive?, comment? }; map them onto the reputational fields.
        const mapped: Partial<ForeignPepReport> = {
            ...('falsePositive' in update ? { reputationalFalsePositive: update.falsePositive } : {}),
            ...('comment' in update ? { reputationalComment: update.comment } : {}),
        };
        setData(prev => {
            if (!prev || prev instanceof Error) return prev;
            return {
                ...prev,
                foreignPepReports: prev.foreignPepReports?.map(r => r.id === reportId ? { ...r, ...mapped } : r)
            };
        });
        setForeignRepModal_report(prev => prev && prev.id === reportId ? { ...prev, ...mapped } : prev);
    };

    const [pepModal_isOpen, setPepModal_isOpen] = useState(false);
    const [pepModal_data, setPepModal_data] = useState<IPepArray[] | undefined>(undefined);

    const [toastShow, setToastShow] = useState(false);
    const [toastType, setToastType] = useState<AlertType>("success");
    const [toastMessage, setToastMessage] = useState("");

    // Fetch risk score data (stageData)
    useEffect(() => {
        const fetchStageData = async () => {
            try {
                setStageData(null); // Set to loading state
                const response = await fetch(`/DueDiligence/GetRiskscores/${customerId}`);
                if (!response.ok) {
                    // Check for error HTTP status
                    if (response.status === 404) {
                        return;
                    } else if (response.status === 403) {
                        return;
                    }
                    throw new Error(`${await response.text()}`);
                }

                const data: RiskScoreDto[] = await response.json();
                const filteredData = data.filter(rs => {
                    return rs.id.toString() === riskscoreId.toString()
                })
                filteredData.length > 0 && setStageData(filteredData[0]);
            } catch (e) {
                if (e instanceof Error) {
                    Bugsnag.notify(e);
                    setStageData(e);
                }
            }
        }
        fetchStageData();
    }, []);

    useEffect(() => {
        setStageData(prev => {
            // Guard clause: only proceed if we have valid previous state data
            if (!prev || prev instanceof Error) {
                return prev;
            }

            // Guard clause: only update if riskScore exists and has a finished timestamp
            if (!riskScore || !riskScore.riskscoreFinishedTimestamp) {
                return prev;
            }

            // Only update if the risk assessment wasn't previously marked as finished
            // This prevents unnecessary state updates on subsequent renders
            if (!prev.riskscoreFinishedTimestamp) {
                return {
                    ...prev,
                    riskscoreFinishedTimestamp: riskScore.riskscoreFinishedTimestamp
                };
            }

            // No changes needed - risk assessment was already marked as finished
            return prev;
        });
    }, [riskScore]);
    const progressBarLoading = useMemo(() => {
        // If either data source is still loading (null) or hasn't started (undefined), show loading
        return stageData === null || stageData === undefined || data === null || data === undefined;
    }, [stageData, data]);
    const progressBarError = useMemo(() => {
        return stageData instanceof Error || data instanceof Error;
    }, [stageData, data]);
    // Fetch KYC answers data
    useEffect(() => {
        setData(null); // Set to loading state
        const fetchData = async () => {
            try {
                const response = await fetch(`/DueDiligence/GetKYCAnswers?riskscoreId=${riskscoreId}&customerId=${customerId}`);
                const data: AnswerListOverviewDto = await response.json();
                if (!response.ok) {
                    throw new Error('Villa við að sækja gögn um áreiðanleikakönnun.');
                }
                setData(data);
            } catch (e) {
                if (e instanceof Error) {
                    Bugsnag.notify(e);
                    setData(e);
                }
            }
        }
        fetchData();
    }, [riskscoreId, customerId]);

    useEffect(() => {
        if (answerListId && data && !(data instanceof Error)) {
            const index = data.answerLists.findIndex(answerList => answerList.answerlistId === answerListId);
            if (index !== -1) {
                setAnswerListIndex(index);
            }
        }
    }, [data, answerListId]);

    useEffect(() => {
        if (!data || data instanceof Error) return;
        setEditContactModal_data({
            customerId: data.customerId,
            companyName: data.customerName,
            confirmProcurations: data.answerLists[answerListIndex]?.hasProcurationVerification ?? false
        });
    }, [data, answerListIndex]);

    const isConnectedStjornmalalegt = (kennitala: string) => {
        if (data === null || data instanceof Error) return undefined;
        const hasResults = data?.pepReport?.results?.find(entry => entry.ssn === kennitala);
        if (!hasResults) return undefined;
        return hasResults;
    }

    const isConnectedAlthjodlegirValistar = (name: string) => {
        if (data === null || data instanceof Error) return undefined;
        let results: ForeignPepReport[] | undefined;
        const nameParts = name.split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts.length > 1 ? nameParts.slice(-1)[0] : undefined;
        const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : undefined;

        // Check first name
        results = data?.foreignPepReports?.filter(entry => entry.mappedReport?.request?.firstName === firstName);
        // If more than 1 person with first name, check last name if available
        if (results && results.length > 1 && lastName) {
            results = results.filter(entry => entry.mappedReport?.request?.lastName === lastName);
        }
        // If more than 1 person with first and last name, check middle name if available
        if (results && results.length > 1 && middleName) {
            results = results.filter(entry => entry.mappedReport?.request?.middleName === middleName);
        }
        if (!results || results.length === 0) return undefined;
        return results;
    }

    // useMemo for adilar table data
    const adilarTableData = useMemo(() => {
        if (data === null || data instanceof Error || data === undefined) return [];
        const dataToReturn: IAdilarTableData[] = [];
        const althChecked: ForeignPepReport[] = [];
        if (data.keldanReport?.mappedReport?.prokuruhafar?.length > 0) {
            data.keldanReport.mappedReport.prokuruhafar.forEach((member) => {
                const stjornResults = isConnectedStjornmalalegt(member.kennitala);
                const althResults = isConnectedAlthjodlegirValistar(member.nafn);
                if (althResults) althChecked.push(althResults[0]);
                const procurationVerification = data?.procurers?.find(procurer => procurer.ssn === member.kennitala);
                dataToReturn.push({
                    hlutverk: "Prókúruumboð",
                    nafnadila: member.nafn,
                    kennitala: member.kennitala,
                    rikisfang: CountryList.find(country => country.value === member.nationality)?.label ?? '',
                    framvisunskilr: (procurationVerification?.eLoginTimestamp !== null && procurationVerification?.eLoginTimestamp !== undefined && procurationVerification?.eLoginTimestamp !== "")
                        ? procurationVerification.eLoginTimestamp : procurationVerification?.notificationSent ? "Beiðni send" : "",
                    stjornmalalegtStatus: stjornResults?.connected,
                    stjornmalalegtData: stjornResults,
                    althjodlegirvalistarStatus: althResults?.[0]?.hasResults ?? false,
                    althjodlegirvalistarData: althResults?.[0],
                    ordsporsahaetta: '-',
                    dateOfBirth: member.faedDagur,
                    //if heimiliFatfCountry is null then we didnt find the country in our list.
                    //else it would be true or false
                    // from that we can deduce that heimili is very likely iceland
                    // if postnr is not empty string or null then its likely iceland
                    // also if postfang is not empty string or null then VERY likely iceland
                    busetuland: member.heimili,
                    busetulandFatfCountry: member.heimiliFatfCountry,
                    nationalityFatfCountry: member.nationalityFatfCountry,

                });
            });
        }
        if (data.keldanReport?.mappedReport?.owners?.length > 0) {
            data.keldanReport.mappedReport.owners.forEach((member) => {
                if (!dataToReturn.find((adili) => member.nafn === adili.nafnadila)) {
                    const stjornResults = isConnectedStjornmalalegt(member.ssn);
                    const althResults = isConnectedAlthjodlegirValistar(member.nafn);
                    if (althResults) althChecked.push(althResults[0]);
                    dataToReturn.push({
                        hlutverk: "Raunverulegur eigandi",
                        nafnadila: member.nafn,
                        kennitala: member.ssn,
                        rikisfang: member.rikisfang,
                        framvisunskilr: undefined,
                        stjornmalalegtStatus: stjornResults?.connected,
                        stjornmalalegtData: stjornResults,
                        althjodlegirvalistarStatus: althResults?.[0]?.hasResults ?? false,
                        althjodlegirvalistarData: althResults?.[0],
                        ordsporsahaetta: '-',
                        dateOfBirth: member.faedingarmanudur,
                        busetuland: member.busetuland,
                        busetulandFatfCountry: member.busetulandFatfCountry,
                        nationalityFatfCountry: member.nationalityFatfCountry,
                    });
                }
            });
        }
        if (data?.keldanReport?.mappedReport?.contacts && data.keldanReport.mappedReport.contacts.length > 0) {
            // Contact that answered the questionnaire
            if (data.answerLists.length > 0) {
                const answerContact = data.keldanReport.mappedReport.contacts.find(contact => contact.name === data.answerLists[answerListIndex].userName);
                if (answerContact && !dataToReturn.find((adili) => answerContact.name === adili.nafnadila)) {
                    const stjornResults = isConnectedStjornmalalegt(answerContact.ssn);
                    const althResults = isConnectedAlthjodlegirValistar(answerContact.name);
                    if (althResults) althChecked.push(althResults[0]);
                    dataToReturn.push({
                        hlutverk: "Tengiliður",
                        nafnadila: answerContact.name,
                        kennitala: answerContact.ssn,
                        rikisfang: CountryList.find(country => country.value === answerContact.nationality)?.label ?? '',
                        framvisunskilr: data.answerLists[answerListIndex].userELoginTimestamp,
                        stjornmalalegtStatus: stjornResults?.connected,
                        stjornmalalegtData: stjornResults,
                        althjodlegirvalistarStatus: althResults?.[0]?.hasResults ?? false,
                        althjodlegirvalistarData: althResults?.[0],
                        ordsporsahaetta: '-', // not implemented yet
                        busetuland: answerContact?.heimiliFatfCountry === null ||
                            answerContact?.areaCode !== '' || answerContact?.areaCode !== null ||
                            answerContact?.region !== '' || answerContact?.region !== null ? 'Ísland' : answerContact.address,
                        busetulandFatfCountry: answerContact?.heimiliFatfCountry,
                        nationalityFatfCountry: answerContact?.nationalityFatfCountry,
                    });
                } else if (!dataToReturn.find((adili) => data.answerLists[answerListIndex].userName === adili.nafnadila)) {
                    // Fallback if contact not found in report contacts
                    const stjornResults = isConnectedStjornmalalegt(data.answerLists[answerListIndex].userSSN);
                    const althResults = isConnectedAlthjodlegirValistar(data.answerLists[answerListIndex].userName);
                    if (althResults) althChecked.push(althResults[0]);
                    dataToReturn.push({
                        hlutverk: "Tengiliður",
                        nafnadila: data.answerLists[answerListIndex].userName,
                        kennitala: data.answerLists[answerListIndex].userSSN,
                        rikisfang: '',
                        framvisunskilr: undefined,
                        stjornmalalegtStatus: stjornResults?.connected,
                        stjornmalalegtData: stjornResults,
                        althjodlegirvalistarStatus: althResults?.[0]?.hasResults ?? false,
                        althjodlegirvalistarData: althResults?.[0],
                        ordsporsahaetta: '', // not implemented yet
                        busetuland: answerContact?.heimiliFatfCountry === null ||
                            answerContact?.areaCode !== '' || answerContact?.areaCode !== null ||
                            answerContact?.region !== '' || answerContact?.region !== null ? 'Ísland' : answerContact.address,
                        busetulandFatfCountry: '',
                        nationalityFatfCountry: undefined,
                    });
                }
            } else {
                // No answer, so we take contact currently assigned from report
                const activeContact = data.keldanReport?.mappedReport?.contacts?.find(contact => contact.name === data.contact.name);
                if (activeContact && !dataToReturn.find((adili) => activeContact.name === adili.nafnadila)) {
                    const rikisfang = (activeContact.nationality === null ||
                        activeContact.areaCode !== '' || activeContact.areaCode !== null ||
                        activeContact.region !== '' || activeContact.region !== null)
                        ? 'Ísland'
                        : CountryList.find(country => country.value === activeContact.nationality)?.label || '';
                    const busetuland = (activeContact.heimiliFatfCountry === null ||
                        activeContact.areaCode !== '' || activeContact.areaCode !== null ||
                        activeContact.region !== '' || activeContact.region !== null) ? 'Ísland' : activeContact.address;

                    dataToReturn.push({
                        hlutverk: "Tengiliður",
                        nafnadila: activeContact.name,
                        kennitala: activeContact.ssn ?? '',
                        rikisfang: rikisfang,
                        framvisunskilr: undefined,
                        stjornmalalegtStatus: undefined, // won't have this if no answer
                        althjodlegirvalistarStatus: undefined, // won't have this if no answer
                        ordsporsahaetta: '-',
                        nationalityFatfCountry: activeContact.nationalityFatfCountry,
                        //These are only needed for prokurors and owners so we can leave them empty here
                        busetuland: busetuland,
                        busetulandFatfCountry: activeContact.heimiliFatfCountry,
                    });
                }
            }
        } else if (data.contact) {
            // No report, so we use assigned contact
            if (!dataToReturn.find((adili) => data.contact.name === adili.nafnadila)) {
                const stjornResults = isConnectedStjornmalalegt(data.contact.ssn);
                const althResults = isConnectedAlthjodlegirValistar(data.contact.name);
                if (althResults) althChecked.push(althResults[0]);
                const busetuland = data.contact.addressCountryChecked === null && (data.contact?.addressFlagged === null
                    || data.contact?.areaCode === null
                    || data.contact.areaCode !== ""
                    || data.contact?.region === null
                    || data.contact.region !== "")
                    ? 'Ísland'
                    : getCountryName(data.contact?.addressCountryChecked)


                const rikisfang = getCountryName(data.contact?.nationality)
                dataToReturn.push({
                    hlutverk: "Tengiliður",
                    nafnadila: data.contact.name,
                    kennitala: data.contact.ssn ?? '',
                    framvisunskilr: undefined,
                    stjornmalalegtStatus: stjornResults?.connected,
                    stjornmalalegtData: stjornResults,
                    althjodlegirvalistarStatus: althResults?.[0].hasResults,
                    althjodlegirvalistarData: althResults?.[0],
                    ordsporsahaetta: '',
                    rikisfang: rikisfang,
                    busetuland: busetuland,

                    nationalityFatfCountry: data.contact.nationalityFlagged ? data.contact.nationality : '',
                    busetulandFatfCountry: data.contact.addressFlagged ? data.contact.addressCountryChecked : '',
                });
            }
        }
        return dataToReturn;
    }, [data, answerListIndex]);

    const renderBody = () => {
        if (data instanceof Error) return <Alert type="error" text={data.message} headText={data.message} />;
        else if (data === undefined) return <Alert type="error" headText="Villa við að sækja gögn um áreiðanleikakönnun." />
        return <>
            {
                // Data is null: we haven't fetched yet, show loading
                data === null ? (
                    <CompanyTitleLoadingSkeleton />
                ) : // Data is not null: we have fetched, but customerSSN might be null
                    data.customerSSN === null || data.customerSSN === undefined ? (
                        // Show fallback CompanyTitle without an ID (or with a different fallback UI)
                        <InfoHeader noMargin data={[{
                            title: "Fyrirtæki",
                            value: data.customerName || "Nafn ekki til staðar"
                        }, {
                            title: "Land",
                            value: CountryList.find(country => country.value === data?.customerCountry)?.label || 'Land ekki til staðar'
                        }]} />
                    ) : (
                        // We have data and customerSSN, show CompanyTitle normally
                        <CompanyTitle withLinkToCompanyPage disableMargin showLoading id={data.customerSSN} accessToken={accessToken} />
                    )
            }
            {/* 
                KYC PROGRESS BAR 
                Only render when both data sources are loaded
                Show error state if either fetch failed
                Otherwise show the progress bar with the fetched data
            */}
            {progressBarError ? (
                // Show error alert if either fetch failed
                <Alert type="error" headText="Ekki tókst að sækja gögn" />
            ) : (
                // Show progress bar - it will handle its own loading state internally
                <KYCSurveyProgressBar
                    type="company"
                    loading={progressBarLoading}
                    konnunHafin={
                        stageData && !(stageData instanceof Error)
                            ? stageData.reportTimestamp
                            : undefined
                    }
                    tengilidurSkradur={
                        data && !(data instanceof Error)
                            ? data.contact?.email
                            : undefined
                    }
                    konnunSend={
                        // adding the backup so that if stageData is not available and the user for example sends the questionnaire 
                        // then the backup dateSent from answerLists will be updated right away and will be used. On refresh that stageData.dateSent will have updated.
                        stageData && !(stageData instanceof Error)
                            ? stageData.dateSent
                            : undefined
                    }
                    ahaettumatSkrad={
                        stageData && !(stageData instanceof Error)
                            ? stageData.riskscoreFinishedTimestamp
                            : undefined
                    }
                    konnunSvarad={
                        stageData && !(stageData instanceof Error)
                            ? stageData.answeredTimestamp || data?.answerLists?.[answerListIndex]?.finishedTimestamp as string
                            : undefined
                    }
                    link={undefined}
                />
            )}
            {/* <CompanyTitle showLoading id={data?.customerSSN} accessToken={accessToken}/> */}
            {/* questionaire-results / questionaire-overview */}
            {
                data?.answerLists && data.answerLists.length > 0 && <QuestionaireResults
                    riskScore={riskScore}
                    setRiskScoreModal_isOpen={setRiskScoreModal_isOpen}
                    setRiskScoreModal_data={setRiskScoreModal_data}
                    customerName={data.customerName} />
            }

            {/* Error state */}
            {data?.errorState && <Alert type="error" headText="Villa" text={data?.errorMessage ?? ""} />}
            {/* Gildandi skráning - aðilar */}
            {riskscoreId !== null &&
                <GildandiSkraningAdilar
                    createdTimestamp={data?.keldanReport?.created_timestamp}
                    customerId={data?.customerId}
                    adilarTableData={adilarTableData}
                    riskScore={riskScore}
                    showLoading={(adilarTableData?.length === 0 && !data)}
                    setEditContactModal_contacts={setEditContactModal_contacts}
                    setEditContactModal_isOpen={setEditContactModal_isOpen}
                    setForeignPepModal_report={setForeignPepModal_report}
                    setForeignPepModal_isOpen={setForeignPepModal_isOpen}
                    setForeignRepModal_report={setForeignRepModal_report}
                    setForeignRepModal_isOpen={setForeignRepModal_isOpen}
                    setPepModal_data={setPepModal_data}
                    setPepModal_isOpen={setPepModal_isOpen}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                />
            }
            {/* Gildandi skráning - grunnupplýsingar (open by default if errorState) */}
            <GildandiSkraningGrunnupplysingar
                data={data}
                initialOpen={!!data?.errorState || riskscoreId === null}
            />

            {/* Svör við áreiðanleikakönnun hjá Kóði ehf. */}
            {data?.answerLists && data.answerLists.length > 0 && <CompanyAnswersList
                answerListIndex={answerListIndex}
                data={data}
                questionListVersion={data.answerLists[answerListIndex].questionListVersion as availableVersions}
                questionListType={data.answerLists[answerListIndex].questionListType as QuestionListType}
            />}

            {/* Alþjóðlegir válistar */}
            {(!data?.errorState || riskscoreId !== null) && <AlthjodlegirValistar
                searchHistory={data?.foreignCompanyPepReports ?? []}
                riskscoreId={riskscoreId}
                initialName={data?.customerName ?? ''}
                initialCountryCode={data?.customerCountry ?? 'IS'} />}

            {/* Fylgiskjöl. */}
            {
                !data?.errorState &&
                data?.answerLists[answerListIndex] && data.answerLists[answerListIndex].answerlistId &&
                <DisplayBox id="file-upload" title="Fylgiskjöl">
                    <p className="paragraph-small--bold instruction-text">Settu inn allt að 10 skjöl:</p>
                    <FileUploadDropzone initialFiles={data.answerLists[answerListIndex].serviceCompanyFiles} answerListId={data.answerLists[answerListIndex].answerlistId} />
                </DisplayBox>
            }
            {/* // logs */}
            <Logs data={data} />
        </>

    }

    return (
        <>
            <div className="KCL_DueDiligence-ViewCompanyAnswers">
                <KYCNavBar />
                <div className="ViewCompanyAnswers__content">
                    <div className="shell page_content">
                        <div className="section__header">
                            <div className="section__header--left">
                                <GoBackLink fallbackUrl="/Areidanleikakannanir/Yfirlit" />
                                <h1 title="Áreiðanleikakönnun">
                                    Áreiðanleikakönnun
                                </h1>
                            </div>
                            <div className="section__header--right">
                                <div>

                                <span className="genius-status-label">Viðskiptavinur er:</span>
                                <GeniusLabel active={isActive} />
                                <FontAwesomeIcon className="fa-icon edit-icon" icon={faEdit} onClick={() => setEditCustomerStatusModal_isOpen(true)}/>
                                </div>
                            </div>
                        </div>
                        {renderBody()}
                    </div >
                </div>
                <RiskScoreModal
                    show={riskScoreModal_isOpen}
                    close={() => setRiskScoreModal_isOpen(false)}
                    data={riskScoreModal_data}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    riskscore={riskScore}
                    setRiskscore={setRiskScore}
                />
                <EditCustomerStatusModal
                    show={editCustomerStatusModal_isOpen}
                    close={() => setEditCustomerStatusModal_isOpen(false)}
                    customerId={parseInt(customerId)}
                    customerName={data && !(data instanceof Error) ? data.customerName : ''}
                    customerType="company"
                    initialActive={isActive}
                    initialActiveDescription={currentActiveDescription}
                    onStatusChanged={(active, activeDescription) => {
                        setIsActive(active);
                        setCurrentActiveDescription(activeDescription);
                    }}
                    onDeleted={() => window.location.href = '/Areidanleikakannanir/Yfirlit'}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                />
                <EditContactModal
                    show={editContactModal_isOpen}
                    close={() => setEditContactModal_isOpen(false)}
                    data={editContactModal_data}
                    contacts={editContactModal_contacts}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    sendNotification={true}
                    riskScore={riskScore}
                    setRiskScore={setRiskScore}
                    employees={editContactModal_contacts?.employees ?? []}
                />
                <ForeignPepModal
                    show={foreignPepModal_isOpen}
                    close={() => setForeignPepModal_isOpen(false)}
                    data={bucketMatches(foreignPepModal_report?.mappedReport?.match, 'international')}
                    request={foreignPepModal_report?.mappedReport?.request}
                    pepReportId={foreignPepModal_report?.id}
                    falsePositive={foreignPepModal_report?.falsePositive}
                    comment={foreignPepModal_report?.comment}
                    onReviewChange={handleForeignPepReviewChange}
                />
                <ForeignPepModal
                    show={foreignRepModal_isOpen}
                    close={() => setForeignRepModal_isOpen(false)}
                    data={bucketMatches(foreignRepModal_report?.mappedReport?.match, 'reputational')}
                    request={foreignRepModal_report?.mappedReport?.request}
                    pepReportId={foreignRepModal_report?.id}
                    falsePositive={foreignRepModal_report?.reputationalFalsePositive}
                    comment={foreignRepModal_report?.reputationalComment}
                    onReviewChange={handleForeignRepReviewChange}
                    bucket="reputational"
                />
                <PepWatchPEPConnectionModal
                    show={pepModal_isOpen}
                    close={() => setPepModal_isOpen(false)}
                    fetchedData={pepModal_data}
                />
            </div >
            <ToastContainer className="toastContainer">
                <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={toastShow}>
                    <Alert type={toastType} headText={toastMessage} />
                </Toast>
            </ToastContainer>
        </>
    )
};

export default ViewAnswersPage;